import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, AlertTriangle, Shield, ShieldAlert, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

import MainLayout from "../../layouts/MainLayout/MainLayout";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import styles from "./RiskControlCenter.module.scss";
import Input from "../../components/Input/Input";
import Select from "../../components/Select/Select";
import Table from "../../components/Table/Table";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import { formatDate } from "../../utils/helpers";
import { showModal } from "../../redux/modalStore";
import RiskControlCenterRiskModal from "./RiskControlCenterRiskModal";

const LEVEL_ORDER = ["high", "medium", "low", "none"];

const normalizeLevel = (level) => {
  const normalized = String(level || "none").trim().toLowerCase();
  if (LEVEL_ORDER.includes(normalized)) return normalized;
  return "none";
};

const getRiskSearchText = (risk) =>
  [
    risk?.risk_id,
    risk?.risk_num,
    risk?.qms_id,
    risk?.qms_description,
    risk?.description,
    risk?.reason,
    risk?.level,
  ]
    .filter((value) => value !== null && value !== undefined)
    .join(" ")
    .toLowerCase();

const RiskControlCenter = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useFetchOrganizationData();

  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");

  const { risksData, findingsData, improvementsData } = useSelector(
    (state) => state.organization,
  );

  const riskItems = useMemo(() => Object.values(risksData || {}), [risksData]);

  const findingsItems = useMemo(
    () => Object.values(findingsData || {}).filter((item) => !item?.archived),
    [findingsData],
  );

  const improvementItems = useMemo(
    () =>
      Object.values(improvementsData || {}).filter((item) => !item?.archived),
    [improvementsData],
  );

  const overviewMetrics = useMemo(() => {
    const counts = { total: 0, active: 0, high: 0, medium: 0, low: 0, none: 0 };

    riskItems.forEach((risk) => {
      counts.total += 1;
      const level = normalizeLevel(risk?.level);
      counts[level] += 1;
      if (level !== "none") counts.active += 1;
    });

    const openFindings = findingsItems.filter(
      (finding) => String(finding?.status || "").toLowerCase() !== "done",
    ).length;

    const activeImprovements = improvementItems.filter(
      (improvement) =>
        String(improvement?.status || "").toLowerCase() !== "done",
    ).length;

    const latestRiskDate = riskItems
      .map((risk) => (risk?.date ? new Date(risk.date) : null))
      .filter(Boolean)
      .sort((a, b) => b - a)[0];

    return {
      ...counts,
      openFindings,
      activeImprovements,
      latestRiskDate: latestRiskDate ? latestRiskDate.toISOString() : null,
    };
  }, [findingsItems, improvementItems, riskItems]);

  const metricCards = useMemo(
    () => [
      {
        key: "active",
        label: t("riskControlCenter.overview.cards.activeRisks"),
        value: overviewMetrics.active,
        icon: <Shield size={18} />,
        accentClass: styles.metricIconNeutral,
      },
      {
        key: "high",
        label: t("riskControlCenter.overview.cards.highRisks"),
        value: overviewMetrics.high,
        icon: <ShieldAlert size={18} />,
        accentClass: styles.metricIconWarning,
      },
      {
        key: "findings",
        label: t("riskControlCenter.overview.cards.openFindings"),
        value: overviewMetrics.openFindings,
        icon: <AlertCircle size={18} />,
        accentClass: styles.metricIconInfo,
      },
      {
        key: "improvements",
        label: t("riskControlCenter.overview.cards.activeImprovements"),
        value: overviewMetrics.activeImprovements,
        icon: <TrendingUp size={18} />,
        accentClass: styles.metricIconActive,
      },
    ],
    [overviewMetrics, t],
  );

  const recommendedActions = useMemo(() => {
    const actions = [];

    if (overviewMetrics.high > 0) {
      actions.push({
        key: "high",
        icon: <ShieldAlert size={18} />,
        title: t("riskControlCenter.recommendedActions.highRisks.title"),
        description: t(
          "riskControlCenter.recommendedActions.highRisks.description",
          { count: overviewMetrics.high },
        ),
        cta: t("riskControlCenter.recommendedActions.highRisks.cta"),
        onClick: () => navigate(`/${orgId}/risk-profile`),
      });
    }

    if (overviewMetrics.openFindings > 0) {
      actions.push({
        key: "findings",
        icon: <AlertTriangle size={18} />,
        title: t("riskControlCenter.recommendedActions.openFindings.title"),
        description: t(
          "riskControlCenter.recommendedActions.openFindings.description",
          { count: overviewMetrics.openFindings },
        ),
        cta: t("riskControlCenter.recommendedActions.openFindings.cta"),
        onClick: () => navigate(`/${orgId}/findings`),
      });
    }

    if (overviewMetrics.activeImprovements > 0) {
      actions.push({
        key: "improvements",
        icon: <TrendingUp size={18} />,
        title: t(
          "riskControlCenter.recommendedActions.activeImprovements.title",
        ),
        description: t(
          "riskControlCenter.recommendedActions.activeImprovements.description",
          { count: overviewMetrics.activeImprovements },
        ),
        cta: t("riskControlCenter.recommendedActions.activeImprovements.cta"),
        onClick: () => navigate(`/${orgId}/improvement-projects`),
      });
    }

    if (!actions.length) {
      actions.push({
        key: "none",
        icon: <Shield size={18} />,
        title: t("riskControlCenter.recommendedActions.none.title"),
        description: t("riskControlCenter.recommendedActions.none.description"),
      });
    }

    return actions;
  }, [
    navigate,
    orgId,
    overviewMetrics.activeImprovements,
    overviewMetrics.high,
    overviewMetrics.openFindings,
    t,
  ]);

  const levelFilterOptions = useMemo(
    () => [
      { id: "all", label: t("riskControlCenter.filters.allLevels") },
      { id: "high", label: t("tableAccordion.level.high") },
      { id: "medium", label: t("tableAccordion.level.medium") },
      { id: "low", label: t("tableAccordion.level.low") },
      { id: "none", label: t("tableAccordion.level.none") },
    ],
    [t],
  );

  const filteredRisks = useMemo(() => {
    const q = String(searchQuery).trim().toLowerCase();
    const normalizedLevel = String(levelFilter || "all").toLowerCase();

    return riskItems
      .filter((risk) => {
        if (!q) return true;
        return getRiskSearchText(risk).includes(q);
      })
      .filter((risk) => {
        if (normalizedLevel === "all") return true;
        return normalizeLevel(risk?.level) === normalizedLevel;
      })
      .sort((a, b) => {
        const dateA = a?.date ? new Date(a.date).getTime() : 0;
        const dateB = b?.date ? new Date(b.date).getTime() : 0;
        if (dateA !== dateB) return dateB - dateA;
        return String(a?.risk_id || "").localeCompare(String(b?.risk_id || ""));
      });
  }, [levelFilter, riskItems, searchQuery]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "risk_id",
        header: t("riskControlCenter.table.riskId"),
        size: 140,
        cell: (props) => (
          <span className={styles.riskIdPill}>
            {props.getValue() || t("tableAccordion.notDefined")}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "qms_id",
        header: t("riskControlCenter.table.qms"),
        size: 110,
        cell: (props) => <div>{props.getValue() || "-"}</div>,
        enableSorting: false,
      },
      {
        accessorKey: "description",
        header: t("riskControlCenter.table.description"),
        size: 360,
        cell: (props) => (
          <div className={styles.descriptionCell}>
            {props.getValue() || t("tableAccordion.notDefined")}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "level",
        header: t("riskControlCenter.table.level"),
        size: 140,
        cell: (props) => {
          const level = normalizeLevel(props.getValue());
          return (
            <span
              className={classNames(styles.levelPill, styles[`pill_${level}`])}
            >
              {t(`tableAccordion.level.${level}`)}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "reason",
        header: t("riskControlCenter.table.reason"),
        size: 420,
        cell: (props) => (
          <div className={styles.reasonCell}>
            {props.getValue() || t("riskProfile.noReason")}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "date",
        header: t("riskControlCenter.table.updated"),
        size: 140,
        cell: (props) => (
          <div>{props.getValue() ? formatDate(props.getValue()) : "-"}</div>
        ),
        enableSorting: false,
      },
    ],
    [t],
  );

  const handleRowClick = (row) => {
    const riskNum = row?.original?.risk_num;
    if (!riskNum && riskNum !== 0) return;

    dispatch(
      showModal({
        name: "risk_control_center_risk_modal",
        data: { riskNum },
      }),
    );
  };

  return (
    <MainLayout>
      <div className={styles.column}>
        <div className={styles.container}>
          <div className={styles.pageHeader}>
            <div className={styles.titleGroup}>
              <div className={styles.titleIcon}>
                <Shield size={32} aria-hidden="true" />
              </div>
              <div className={styles.pageTitle}>{t("riskControlCenter.title")}</div>
            </div>
            <div className={styles.pageSubtitle}>
              {t("riskControlCenter.subtitle")}
            </div>
          </div>

          <div className={styles.tabsRow}>
            <div className={styles.tabSwitcher} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "overview"}
                className={classNames(styles.tabButton, {
                  [styles.tabButtonActive]: activeTab === "overview",
                })}
                onClick={() => setActiveTab("overview")}
              >
                {t("riskControlCenter.tabs.overview")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "risks"}
                className={classNames(styles.tabButton, {
                  [styles.tabButtonActive]: activeTab === "risks",
                })}
                onClick={() => setActiveTab("risks")}
              >
                {t("riskControlCenter.tabs.risks")}
              </button>
            </div>
            <button
              type="button"
              className={styles.actionGhostButton}
              onClick={() => navigate(`/${orgId}/risk-profile`)}
            >
              <Shield size={16} />
              {t("riskControlCenter.header.openRiskProfile")}
            </button>
          </div>

          {activeTab === "overview" && (
            <>
              <div className={styles.metricGrid}>
                {metricCards.map((card) => (
                  <div key={card.key} className={styles.metricCard}>
                    <div className={styles.metricHeader}>
                      <span className={styles.metricTitle}>{card.label}</span>
                      <span
                        className={classNames(
                          styles.metricIconBadge,
                          card.accentClass,
                        )}
                      >
                        {card.icon}
                      </span>
                    </div>
                    <div className={styles.metricValue}>{card.value}</div>
                    {card.key === "active" && overviewMetrics.latestRiskDate && (
                      <div className={styles.metricSubtitle}>
                        {t("riskControlCenter.overview.cards.lastUpdated", {
                          date: formatDate(overviewMetrics.latestRiskDate),
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className={styles.contentGrid}>
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>
                      {t("riskControlCenter.recommendedActions.title")}
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.actionsList}>
                      {recommendedActions.map((action) => (
                        <div key={action.key} className={styles.actionRow}>
                          <span className={styles.actionIcon}>
                            {action.icon}
                          </span>
                          <div className={styles.actionCopy}>
                            <div className={styles.actionTitle}>
                              {action.title}
                            </div>
                            <div className={styles.actionDescription}>
                              {action.description}
                            </div>
                            {action.onClick && action.cta && (
                              <button
                                type="button"
                                className={styles.actionLink}
                                onClick={action.onClick}
                              >
                                {action.cta}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>
                      {t("riskControlCenter.quickLinks.title")}
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.quickLinks}>
                      <button
                        type="button"
                        className={styles.quickLink}
                        onClick={() => navigate(`/${orgId}/risk-profile`)}
                      >
                        <Shield size={16} />
                        <span>
                          {t("riskControlCenter.quickLinks.riskProfile")}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.quickLink}
                        onClick={() => navigate(`/${orgId}/risk-analysis`)}
                      >
                        <TrendingUp size={16} />
                        <span>
                          {t("riskControlCenter.quickLinks.riskAnalysis")}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.quickLink}
                        onClick={() => navigate(`/${orgId}/findings`)}
                      >
                        <AlertCircle size={16} />
                        <span>{t("riskControlCenter.quickLinks.findings")}</span>
                      </button>
                      <button
                        type="button"
                        className={styles.quickLink}
                        onClick={() => navigate(`/${orgId}/improvement-projects`)}
                      >
                        <TrendingUp size={16} />
                        <span>
                          {t(
                            "riskControlCenter.quickLinks.improvementProjects",
                          )}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "risks" && (
            <div className={styles.tableSection}>
              <div className={styles.tableControls}>
                <Input
                  wrapperClassName={styles.searchInputWrapper}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("riskControlCenter.filters.searchPlaceholder")}
                  icon={SearchIcon}
                  isClearable
                  onClear={() => setSearchQuery("")}
                  aria-label={t("riskControlCenter.filters.searchPlaceholder")}
                />
                <Select
                  value={levelFilter}
                  onChange={setLevelFilter}
                  options={levelFilterOptions}
                  variant="filters"
                  className={styles.levelSelect}
                />
              </div>

              <div className={styles.tableCard}>
                <Table
                  columns={columns}
                  data={filteredRisks}
                  onRowClick={handleRowClick}
                  rowHover
                  variant="riskControlCenter"
                />
                {!filteredRisks.length && (
                  <div className={styles.tableEmptyState}>
                    <div className={styles.tableEmptyCopy}>
                      {t("riskControlCenter.table.empty")}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <RiskControlCenterRiskModal risksData={risksData} />
      </div>
    </MainLayout>
  );
};

export default RiskControlCenter;
